import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { ErrorHandlerService } from '@lib/services/error/error-handler.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Enquiry form payload interface
export interface EnquiryFormPayload {
    firstName: string;
    lastName: string;
    organisation: string;
    designation: string;
    email: string;
    mobile: string;
    notes: string;
    preferredCourse?: string;
    preferredFaculty?: string;
    recaptchaToken: string;
}

// API response interfaces
export interface EnquirySuccessResponse {
    status: 'success';
    message: string;
    enquiryId: string;
}

export interface EnquiryErrorResponse {
    status: 'error';
    message: string;
    errors: Array<{
        field: string;
        error: string;
    }>;
}

export type EnquiryApiResponse = EnquirySuccessResponse | EnquiryErrorResponse;

// Validation error interface
export interface ValidationError {
    field: string;
    error: string;
}

@Injectable({
    providedIn: 'root',
})
export class EnquiryService {
    private readonly errorHandlerService = inject(ErrorHandlerService);

    constructor(private readonly http: HttpClient) {}

    /**
     * Submit an enquiry form
     * @param courseId - The course ID from query params
     * @param payload - The enquiry form data
     * @returns Observable with success response or error details
     */
    submitEnquiry(courseId: string, payload: EnquiryFormPayload): Observable<EnquirySuccessResponse> {
        const url = `${environment.API_URL}/students/course/${courseId}/enquiry`;

        return this.http.post<any>(url, payload).pipe(
            map((response: any) => {
                const status = response?.status;
                const isStringSuccess = typeof status === 'string' && status.toLowerCase() === 'success';
                const isBooleanSuccess = response?.success === true;

                if (isStringSuccess || isBooleanSuccess || status === undefined) {
                    const successResponse: EnquirySuccessResponse = {
                        status: 'success',
                        message: response?.message ?? 'Enquiry submitted successfully',
                        enquiryId: response?.enquiryId ?? response?.data?.enquiryId ?? '',
                    };
                    return successResponse;
                }

                throw new Error(response?.message || 'Unknown error occurred');
            }),
            catchError((err) => (err instanceof HttpErrorResponse ? this.handleError(err) : throwError(() => err))),
        );
    }

    /**
     * Handle HTTP errors and provide meaningful error messages
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        // Use enhanced error handler for consistent error messages
        const errorMessage = this.errorHandlerService.getErrorMessage(error, {
            context: 'enquiry',
            defaultMessage: 'An unexpected error occurred. Please try again.',
            fallbackToApiMessage: true,
            showFriendlyMessage: true,
        });

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Validate enquiry payload before submission
     * @param payload - The enquiry form data to validate
     * @returns Array of validation errors, empty if valid
     */
    validateEnquiryPayload(payload: EnquiryFormPayload): ValidationError[] {
        const errors: ValidationError[] = [];

        // Required field validations
        if (!payload.firstName?.trim()) {
            errors.push({ field: 'firstName', error: 'First name is required' });
        }

        if (!payload.lastName?.trim()) {
            errors.push({ field: 'lastName', error: 'Last name is required' });
        }

        if (!payload.organisation?.trim()) {
            errors.push({ field: 'organisation', error: 'Organisation is required' });
        }

        if (!payload.designation?.trim()) {
            errors.push({ field: 'designation', error: 'Designation is required' });
        }

        if (!payload.email?.trim()) {
            errors.push({ field: 'email', error: 'Email is required' });
        } else if (!this.isValidEmail(payload.email)) {
            errors.push({ field: 'email', error: 'Invalid email format' });
        }

        if (!payload.notes?.trim()) {
            errors.push({ field: 'notes', error: 'Details of enquiry is required' });
        }

        // Optional field validations
        if (payload.mobile && !this.isValidMobileNumber(payload.mobile)) {
            errors.push({ field: 'mobile', error: 'Invalid mobile number format' });
        }

        if (payload.preferredCourse && payload.preferredCourse.trim().length < 2) {
            errors.push({ field: 'preferredCourse', error: 'Preferred course must be at least 2 characters' });
        }

        if (payload.preferredFaculty && payload.preferredFaculty.trim().length < 2) {
            errors.push({ field: 'preferredFaculty', error: 'Preferred faculty must be at least 2 characters' });
        }

        return errors;
    }

    /**
     * Validate email format
     * @param email - Email string to validate
     * @returns boolean indicating if email is valid
     */
    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; //NOSONAR
        return emailRegex.test(email);
    }

    /**
     * Validate mobile number format (E.164)
     * @param mobile - Mobile number string to validate
     * @returns boolean indicating if mobile number is valid
     */
    private isValidMobileNumber(mobile: string): boolean {
        const mobileRegex = /^\+[1-9]\d{1,14}$/;
        return mobileRegex.test(mobile);
    }

    /**
     * Format mobile number to E.164 format
     * @param countryCode - Country code (e.g., '91' for India)
     * @param mobileNumber - Mobile number without country code
     * @returns Formatted mobile number in E.164 format
     */
    formatMobileNumber(countryCode: string, mobileNumber: string): string {
        // Handle null/undefined cases
        if (!mobileNumber || typeof mobileNumber !== 'string') {
            return '';
        }

        // Remove any non-digit characters from mobile number
        const cleanNumber = mobileNumber.replaceAll(/\D/g, '');

        // Ensure country code starts with + and mobile number is clean
        const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;

        return `${formattedCountryCode}${cleanNumber}`;
    }
}
